import { useQuery, useQueryClient } from '@tanstack/react-query'
import { defaultTile } from 'assets'
import { useCurrentUser, useWorkspace } from 'components/providers'
import { pick } from 'lodash-es'
import { useParams } from 'react-router'
import { DBProps, IFacility, dbProps } from 'types'
import { getDocs, getFileUrl, useWorkspaces } from '.'

export type FacilityRecord = PartialFacility & ExtraFacilityRecordProps

type PartialFacility = Pick<IFacility, keyof DBProps | (typeof partialFacilityFields)[number]>

interface ExtraFacilityRecordProps {
  imageUrl?: string
}

const partialFacilityFields = [
  'hasImage',
  'location',
  'managerId',
  'title',
  'workspaceId',
] as const satisfies string[]

const makeQueryKey = (workspaceIds?: string[]) => ['facilities', workspaceIds]

export function useFacilities(workspaceId?: string) {
  const workspaceIdParam = useParams().workspaceId
  const _workspaceId = workspaceId || workspaceIdParam
  const { workspaces } = useWorkspaces('emp', true)
  const workspaceIds =
    _workspaceId === 'all' ? workspaces?.map(w => w.id) : _workspaceId ? [_workspaceId] : []

  const { data, ...rest } = useQuery({
    queryKey: makeQueryKey(workspaceIds),
    queryFn: () => getFacilities(workspaceIds!),
    staleTime: 1000 * 30,
    enabled: !!workspaceIds?.length,
  })

  return { facilities: data, ...rest }
}

export function useMutateFacilityRecords() {
  const user = useCurrentUser()
  const { workspace } = useWorkspace()
  const queryClient = useQueryClient()

  async function updateFacilityRecord(body: IFacility) {
    if (!workspace) return

    const record = await makeFacilityRecord(body)

    queryClient.setQueriesData<FacilityRecord[]>(
      { queryKey: makeQueryKey([workspace.id]) },
      facilities => {
        if (!facilities) return [record]

        const facilityIndex = facilities.findIndex(e => e.id === record.id)
        if (facilityIndex < 0) return sortFacilityRecords([...facilities, record])

        return sortFacilityRecords(facilities.map((e, i) => (i === facilityIndex ? record : e)))
      }
    )
  }

  async function removeFacilityRecord(id: string) {
    if (!user || !workspace) return

    queryClient.setQueriesData<FacilityRecord[]>(
      { queryKey: makeQueryKey([workspace.id]) },
      facilities => {
        if (!facilities) return []

        const facilityIndex = facilities.findIndex(e => e.id === id)
        if (facilityIndex < 0) return facilities

        return facilities.filter((_, i) => i !== facilityIndex)
      }
    )
  }

  return { updateFacilityRecord, removeFacilityRecord }
}

async function getFacilities(workspaceIds: string[]) {
  const facilities = await getDocs({
    collection: 'facilities',
    fields: partialFacilityFields,
    constraints: [{ field: 'workspaceId', op: 'in', value: workspaceIds }],
    sort: { by: 'title', order: 'ASC' },
  })

  return Promise.all(facilities.data.map(makeFacilityRecord))
}

function sortFacilityRecords(records: FacilityRecord[]) {
  return records.sort((a, b) => (a.title || '').localeCompare(b.title || ''))
}

async function makeFacilityRecord(facility: PartialFacility) {
  const partial = pick(facility, [...dbProps, ...partialFacilityFields])
  const { id, workspaceId, hasImage } = partial
  let imageUrl = defaultTile
  if (hasImage) {
    const url = await getFileUrl(getFacilityImagePath(id), workspaceId)
    if (url) imageUrl = url
  }

  return { ...partial, imageUrl } as FacilityRecord
}

export const getFacilityImagePath = (id: string) => `facilities/${id}/image`
